import { useMutationObserver } from "@vueuse/core";
import { computed, onMounted, shallowRef, watch } from "vue";
import { useAriaLabel } from "#ce/composables/useAriaLabel";
import { MOUNT_READY_MARKER } from "#ce/framework/mount-ready";

/**
 * Names the activator field and the popup listbox, and supplies the activator's tooltip and
 * placeholder.
 *
 * A host attribute does not reach the activator on its own, so the host's `aria-label` is carried
 * across. The host's `<label>` elements name the field's input through `useAriaLabel`, and their
 * text names the listbox, kept current as they change.
 *
 * An icon-only activator shows no text, placeholder included. Its name falls back to the selected
 * item's title, and it earns a tooltip carrying that name.
 *
 * @param {object} props  Owning component's reactive props; reads `label` and `placeholder`.
 * @param {{ hostElement: HTMLElement | null | undefined, field: import("vue").Ref<HTMLElement | null>,
 *   hasSelection: import("vue").Ref<boolean>, iconOnly: import("vue").Ref<boolean>, selectedItem: import("vue").Ref<any>,
 *   selectedTagsEnabled: import("vue").Ref<boolean>,
 *   getItemTitle: (item: any) => any }} options  `field` is the activator's `sp-text-field`.
 * @returns {{ activatorAriaLabel: import("vue").ComputedRef<string|undefined>,
 *   listLabel: import("vue").ComputedRef<string|undefined>,
 *   activatorTooltip: import("vue").ComputedRef<string|undefined>,
 *   activatorPlaceholder: import("vue").ComputedRef<string|undefined> }}
 */
export function useSelectLabels(
  props,
  { hostElement, field, hasSelection, iconOnly, selectedTagsEnabled, selectedItem, getItemTitle },
) {
  const hostLabels = shallowRef([]);

  onMounted(captureHostLabels);
  useMutationObserver(hostLabels, captureHostLabels, {
    childList: true,
    characterData: true,
    subtree: true,
  });

  const activatorLabel = computed(() => {
    const hostLabel = hostElement?.getAttribute("aria-label");

    if (!iconOnly.value) {
      // The rendered `sp-label` points at the picker, not the input inside it, so the visible label
      // names the control here or nothing does.
      return hostLabel || props.label;
    }

    // `find(Boolean)` rather than `??`: an empty title or placeholder is not a name, and naming the
    // control "" is worse than falling through to the next candidate.
    return [getItemTitle(selectedItem.value), props.placeholder, hostLabel, props.label].find(Boolean);
  });

  // The field's input sits in the field's shadow root and exists once the field is mount-ready.
  const activatorInput = shallowRef(null);

  watch(field, syncActivatorInput, {
    flush: "post",
  });
  useMutationObserver(field, syncActivatorInput, {
    attributes: true,
    attributeFilter: [MOUNT_READY_MARKER],
  });

  const { ariaLabel: activatorAriaLabel, elementsText: associatedLabelText } = useAriaLabel(activatorInput, {
    label: activatorLabel,
    elements: hostLabels,
  });

  // Built from what names the control rather than from `activatorLabel`, whose icon-only branch falls
  // back to the selected item's title — a list renamed on every pick. A visible `label` reaches the
  // activator as an `aria-labelledby` relationship, which cannot cross into the list's shadow root,
  // so its text is carried instead. `placeholder` is last and is what names an `sp-autocomplete`,
  // which its documented usage supplies alone.
  const listLabel = computed(() =>
    [hostElement?.getAttribute("aria-label"), props.label, associatedLabelText.value, props.placeholder].find(Boolean),
  );

  // Elsewhere the label a tooltip would carry is not the text on screen, so it would title the
  // control with something other than what it reads.
  const activatorTooltip = computed(() => (iconOnly.value ? activatorLabel.value : undefined));

  const activatorPlaceholder = computed(() =>
    iconOnly.value || (selectedTagsEnabled.value && hasSelection.value) ? undefined : props.placeholder,
  );

  function captureHostLabels() {
    hostLabels.value = [...(hostElement?.labels ?? [])];
  }

  function syncActivatorInput() {
    activatorInput.value = field.value?.inputElement ?? null;
  }

  return {
    activatorAriaLabel,
    listLabel,
    activatorTooltip,
    activatorPlaceholder,
  };
}
